import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type {
  CreateHostInput,
  CreateSoftwareInstanceInput,
  ICenterInfra,
  IHost,
  IHostVerificationRow,
  ISoftwareInstance,
  ISoftwareInventoryRow,
  UpdateHostInput,
  UpdateSoftwareInstanceInput,
  UpsertCenterInfraInput,
} from "@/features/servers/schemas/servers";
import {
  createHost,
  createSoftwareInstance,
  deleteHost,
  deleteSoftwareInstance,
  getCenterInfra,
  listHostVerificationInventory,
  listHosts,
  listSoftwareInstances,
  listSoftwareInventory,
  updateHost,
  updateSoftwareInstance,
  upsertCenterInfra,
} from "@/features/servers/services/centerInfraService";

export const centerInfraKeys = {
  all: ["centerInfra"] as const,
  detail: (uid: string) => [...centerInfraKeys.all, "detail", uid] as const,
  hosts: (uid: string) => [...centerInfraKeys.all, "hosts", uid] as const,
  instances: (uid: string) => [...centerInfraKeys.all, "instances", uid] as const,
  inventory: () => [...centerInfraKeys.all, "inventory"] as const,
  hostVerification: () => [...centerInfraKeys.all, "hostVerification"] as const,
};

export function useCenterInfra(institutionUid: string | undefined) {
  return useQuery<ICenterInfra>({
    queryKey: centerInfraKeys.detail(institutionUid ?? ""),
    queryFn: () => getCenterInfra(institutionUid!),
    enabled: !!institutionUid,
    staleTime: 2 * 60 * 1000,
  });
}

export function useUpsertCenterInfra(institutionUid: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpsertCenterInfraInput) =>
      upsertCenterInfra(institutionUid, input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: centerInfraKeys.detail(institutionUid),
      });
    },
  });
}

export function useHosts(institutionUid: string | undefined) {
  return useQuery<IHost[]>({
    queryKey: centerInfraKeys.hosts(institutionUid ?? ""),
    queryFn: () => listHosts(institutionUid!),
    enabled: !!institutionUid,
    staleTime: 2 * 60 * 1000,
  });
}

export function useHostMutations(institutionUid: string) {
  const queryClient = useQueryClient();

  const invalidate = async () => {
    await queryClient.invalidateQueries({
      queryKey: centerInfraKeys.hosts(institutionUid),
    });
    await queryClient.invalidateQueries({
      queryKey: centerInfraKeys.detail(institutionUid),
    });
    await queryClient.invalidateQueries({
      queryKey: centerInfraKeys.inventory(),
    });
    await queryClient.invalidateQueries({
      queryKey: centerInfraKeys.hostVerification(),
    });
  };

  const create = useMutation({
    mutationFn: (input: CreateHostInput) => createHost(institutionUid, input),
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: ({ hostId, data }: { hostId: string; data: UpdateHostInput }) =>
      updateHost(institutionUid, hostId, data),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (hostId: string) => deleteHost(institutionUid, hostId),
    onSuccess: invalidate,
  });

  return { create, update, remove };
}

export function useSoftwareInstances(institutionUid: string | undefined) {
  return useQuery<ISoftwareInstance[]>({
    queryKey: centerInfraKeys.instances(institutionUid ?? ""),
    queryFn: () => listSoftwareInstances(institutionUid!),
    enabled: !!institutionUid,
    staleTime: 2 * 60 * 1000,
  });
}

export function useSoftwareInstanceMutations(institutionUid: string) {
  const queryClient = useQueryClient();

  const invalidate = async () => {
    await queryClient.invalidateQueries({
      queryKey: centerInfraKeys.instances(institutionUid),
    });
    await queryClient.invalidateQueries({
      queryKey: centerInfraKeys.detail(institutionUid),
    });
    await queryClient.invalidateQueries({
      queryKey: centerInfraKeys.inventory(),
    });
  };

  const create = useMutation({
    mutationFn: (input: CreateSoftwareInstanceInput) =>
      createSoftwareInstance(institutionUid, input),
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: ({
      instanceId,
      data,
    }: {
      instanceId: string;
      data: UpdateSoftwareInstanceInput;
    }) => updateSoftwareInstance(institutionUid, instanceId, data),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (instanceId: string) =>
      deleteSoftwareInstance(institutionUid, instanceId),
    onSuccess: invalidate,
  });

  return { create, update, remove };
}

/**
 * Inventario de software de todos los centros (para reportes).
 */
export function useSoftwareInventory() {
  return useQuery<ISoftwareInventoryRow[]>({
    queryKey: centerInfraKeys.inventory(),
    queryFn: listSoftwareInventory,
    staleTime: 2 * 60 * 1000,
  });
}

/**
 * Inventario de hosts para verificación periódica.
 */
export function useHostVerificationInventory() {
  return useQuery<IHostVerificationRow[]>({
    queryKey: centerInfraKeys.hostVerification(),
    queryFn: listHostVerificationInventory,
    staleTime: 2 * 60 * 1000,
  });
}
